/**
 * Shown when a signed-in user reaches a DecisionPoint global-admin route without the admin claim.
 */
import React, { useState } from 'react';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { useAuth } from 'hooks/useAuth';

import { PRODUCT_NAME } from '../data/fusionMsiBranding';
import { adminPath, authPath, marketingHubPath } from '../marketing/fusionMsiMarketingRouting';

export default function DpGlobalAdminRequired() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const next = `${location.pathname}${location.search}`;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 3,
        textAlign: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="h6" color="primary.main">
        Platform admin access required
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
        The access roster and other global-admin tools require the Firebase admin custom claim
        on your account JWT — the same gate as{' '}
        <Link component={RouterLink} to="/admin/voxpont-beta">
          VoxPont Beta admin
        </Link>
        . Beta invite access alone is not enough.
      </Typography>
      {user?.email && (
        <Typography variant="body2" color="text.secondary">
          Signed in as {user.email}
        </Typography>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <LoadingButton loading={signingOut} variant="outlined" onClick={handleSignOut}>
          Sign out
        </LoadingButton>
        <Button component={RouterLink} to={authPath({ next })} variant="contained" color="primary">
          Sign in with another account
        </Button>
      </Stack>
      <Button component={RouterLink} to={adminPath()} variant="text" color="primary">
        Back to {PRODUCT_NAME} admin
      </Button>
      <Link component={RouterLink} to={marketingHubPath()} color="primary">
        {PRODUCT_NAME} home
      </Link>
    </Box>
  );
}
